import {
  GET_REGISTER_ENTITY_TYPES,
  LOGIN_URL,
  OTP_SENT,
  REGISTER_USER_ENTITY,
  RESOLVE_SUB_ENTITY_ONBOARDING_CONTEXT_URL,
  URL_FORGOT_PASSWORD,
  get_headers,
  normalize_user_role,
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { password_visibility_mixin } from '@/utils/mixins/password_visibility_mixin.js';
import {
  validate_non_empty_string,
  validate_non_sub_zero_number,
  validate_phone_number
} from '@/utils/validations/validate_inputs';
import OnboardingIndex from '@/views/onboarding/OnboardingIndex.vue';
import axios from 'axios';

function parse_response_payload(payload) {
  if (typeof payload !== 'string') {
    return payload || {};
  }

  try {
    return JSON.parse(payload);
  } catch (error) {
    return {};
  }
}

function map_entity_types(rows) {
  return (rows || []).map(entity_type => ({
    id: entity_type.id,
    name: entity_type.entity_type_name,
  }));
}

function build_entity_name(vm) {
  return `${vm.first_name || ''} ${vm.last_name || ''}`.trim();
}

function build_registration_payload(vm) {
  const payload = {
    first_name: vm.first_name,
    last_name: vm.last_name,
    email: vm.email,
    phone_number: vm.phone_number,
    password: vm.password,
    entity_type_id: vm.entity_type_id,
    project_id: vm.dec_project_id,
  };

  if (!vm.sub_entity_context) {
    return payload;
  }

  return {
    ...payload,
    entity_name: build_entity_name(vm),
    form_ids: (vm.sub_entity_context.form_ids || []).map(Number).filter(Boolean),
    parent_application_id: Number(vm.sub_entity_context.parent_application_id),
    parent_entity_profile_id: Number(vm.sub_entity_context.parent_entity_profile_id),
    parent_entity_type_id: Number(vm.sub_entity_context.parent_entity_type_id),
  };
}

function error_message_from_response(error) {
  const error_data = parse_response_payload(error?.response?.data);
  return error_data.message || 'Error: Something went wrong';
}

export default {
  name: 'SignUp',
  mixins: [alert_mixin, password_visibility_mixin],
  components: {
    OnboardingIndex
  },
  data: () => ({
    show_password: false,
    show_confirm_password: false,
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    entity_type_id: '',
    eye_icon: 'bi-eye-slash',
    url_forgot_password: URL_FORGOT_PASSWORD,
    url_login: LOGIN_URL,
    verification_running: false,
    title: 'Sign up to your account',
    submit_button_text: 'Register',
    entity_type_list: [],
    dec_project_id: '',
    token: '',
    user_id: null,
    headers: {},
    from_jobs_advertised: false,
    selected_job_title: '',
    sub_entity_context: null,
    is_loading_context: false,
  }),

  mounted() {
    this.initialize_sign_up();
  },

  methods: {
    is_forced_sub_entity_onboarding() {
      return Boolean(this.$route.params.onboarding_token);
    },

    async initialize_sign_up() {
      if (this.is_forced_sub_entity_onboarding()) {
        await this.load_sub_entity_onboarding_context();
        return;
      }

      this.get_default_entity_types();
    },

    async load_sub_entity_onboarding_context() {
      this.is_loading_context = true;
      try {
        const response = await axios.get(RESOLVE_SUB_ENTITY_ONBOARDING_CONTEXT_URL, {
          params: { onboarding_token: this.$route.params.onboarding_token }
        });
        const response_data = parse_response_payload(response.data);
        if (response_data.status !== 'success') {
          this.show_error(response_data.message || 'Invalid onboarding link.');
          return;
        }

        this.apply_sub_entity_onboarding_context(response_data.data || {});
      } catch (error) {
        this.show_error(error_message_from_response(error));
      } finally {
        this.is_loading_context = false;
      }
    },

    apply_sub_entity_onboarding_context(context) {
      this.sub_entity_context = context;
      this.dec_project_id = context.project_id || '';
      this.entity_type_id = context.child_entity_type_id || '';
      this.entity_type_list = [{
        id: context.child_entity_type_id,
        name: context.child_entity_type_name || 'Sub-Entity'
      }];
      this.title = `Sign up as ${context.child_entity_type_name || 'Sub-Entity'}`;
      this.submit_button_text = 'Create account';
    },

    get_default_entity_types() {
      const project_id = this.$route.params.project_id || '';

      axios.get(GET_REGISTER_ENTITY_TYPES, {
        params: { project_id }
      })
      .then(response => {
        const response_data = parse_response_payload(response.data);
        if (response_data.status !== 'success') {
          this.show_error('Error fetching requests');
          return;
        }

        this.dec_project_id = response_data.project_id;
        this.entity_type_list = map_entity_types(response_data.data);
        this.apply_job_prefill();
      })
      .catch(error => {
        this.show_error(error_message_from_response(error));
      });
    },

    apply_job_prefill() {
      const apply_job_id = sessionStorage.getItem('apply_job_id');
      const apply_job_title = sessionStorage.getItem('apply_job_title');
      if (!apply_job_id) {
        return;
      }

      const job_id_num = Number(apply_job_id);
      const job_match = this.entity_type_list.find(et => et.id === job_id_num);
      const selected_job = job_match || {
        id: job_id_num,
        name: apply_job_title || 'Selected Job'
      };

      if (!job_match) {
        this.entity_type_list.unshift(selected_job);
      }

      this.entity_type_id = selected_job.id;
      this.from_jobs_advertised = true;
      this.selected_job_title = apply_job_title || selected_job.name;
      sessionStorage.removeItem('apply_job_id');
      sessionStorage.removeItem('apply_job_title');
    },

    sign_up_function() {
      if (!this.validate_register_entity_user_inputs()) {
        return;
      }

      axios
        .post(REGISTER_USER_ENTITY, build_registration_payload(this))
        .then((response) => {
          const response_data = parse_response_payload(response.data);
          this.handle_register_success(response_data);
        })
        .catch((error) => {
          this.handle_register_error(error);
        });
    },

    handle_register_success(response_data) {
      if (response_data.status !== 'success') {
        this.show_error(response_data.message || 'Registration failed');
        return;
      }

      this.token = response_data.token;
      this.user_id = response_data.user_id;
      sessionStorage.setItem('user_id', response_data.user_id);
      sessionStorage.setItem('token', response_data.token);
      sessionStorage.setItem('user_role', normalize_user_role(response_data.role_type));
      this.headers = get_headers();
      this.handle_successful_registration();
    },

    handle_register_error(error) {
      const message = error_message_from_response(error);
      if (message === 'Account already exists') {
        this.show_error('Account already exists. Please login instead.');
        this.$router.push('/login');
        return;
      }

      this.show_error(message);
    },

    validate_register_entity_user_inputs() {
      if (this.is_forced_sub_entity_onboarding() && !this.sub_entity_context) {
        this.show_error('This onboarding link is invalid or could not be loaded.');
        return false;
      }

      if (!validate_non_sub_zero_number(this.entity_type_id)) {
        this.show_error('Please select an Entity Type');
        return false;
      }

      if (!validate_non_empty_string(this.first_name)) {
        this.show_error('First name must not be empty');
        return false;
      }

      if (!validate_non_empty_string(this.last_name)) {
        this.show_error('Last name must not be empty');
        return false;
      }

      if (!validate_non_empty_string(this.email)) {
        this.show_error('Email must not be empty');
        return false;
      }

      if (!validate_non_empty_string(this.phone_number)) {
        this.show_error('Phone number must not be empty');
        return false;
      }

      if (this.password !== this.confirm_password) {
        this.show_info('Passwords do not match.');
        return false;
      }

      return true;
    },

    async handle_successful_registration() {
      const data = {
        user_id: this.user_id
      };

      try {
        await axios.post(OTP_SENT, data, {
          headers: this.headers,
        });
        sessionStorage.setItem('first_time_login', true);
        this.$router.push({
          path: '/one_time_pin',
          query: {
            user_id: this.user_id,
            token: this.token,
          }
        });
      } catch (error) {
        this.show_error('Could not send OTP to user.');
      }
    },

    validate_phone_number(phone_number) {
      return validate_phone_number(phone_number);
    }
  },
};
